import './SystemReportSearch.scss'
import { useMemo, useState } from "react";
import { Field, Panel } from '../../../../layouts';
import SelectBox from "devextreme-react/select-box";
import { useGetSystemReports } from "../api/get-system-reports";
import { useAuth } from "../../../../contexts/auth";
import DateBox from "devextreme-react/date-box";
import { SystemReportType } from "../types/SystemReportType";
import { Item as ToolbarItem, Toolbar } from 'devextreme-react/toolbar';
import Button from "devextreme-react/button";
import { Box, Item } from "devextreme-react/box";
import { LoadPopupPanel } from "../../../../components";
import { Popup, ToolbarItem as PopupToolbarItem } from "devextreme-react/popup";
import DataGrid from "devextreme-react/data-grid";
import { useGetSystemReportData } from "../api/get-system-report-data";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";

export default function SystemReportSearch() {
    const { user } = useAuth();
    const systemReports = useGetSystemReports(user?.companyId);

    const [reportCode, setReportCode] = useState<SystemReportType | undefined>();
    const [dateFrom, setDateFrom] = useState<Date | undefined>();
    const [dateTo, setDateTo] = useState<Date | undefined>();
    const [year, setYear] = useState(new Date().getFullYear());
    const [popupVisible, setPopupVisible] = useState<boolean>(false);
    const [popupTitle, setPopupTitle] = useState<string>("");
    const reportData = useGetSystemReportData(user?.companyId, reportCode?.code, dateFrom, dateTo, year, reportCode?.showYear === true);
    const [exportToExcel, setExportToExcel] = useState<boolean>(false);

    const years = useMemo(() => {
        const yearList: number[] = [];
        for (let year = new Date().getFullYear(); year >= 2004; year--) {
            yearList.push(year);
        }
        return yearList;
    }, []);

    return (
        <>
            <Panel header="View System Reports" isLoading={systemReports.isLoading} className="system-reports">
                <Box>
                    <Item ratio={1}>
                    </Item>
                    <Item baseSize={400}>
                        <Field label='Select Report'>
                            <SelectBox
                                value={reportCode}
                                onValueChanged={(e) => setReportCode(e.value)}
                                dataSource={systemReports?.data}
                                displayExpr="name"
                            >
                            </SelectBox>
                        </Field>
                        <Field label='Year' visible={reportCode?.showYear === true}>
                            <SelectBox
                                value={year}
                                onValueChanged={(e) => setYear(e.value)}
                                dataSource={years}
                            >
                            </SelectBox>
                        </Field>
                        <Field label='Date From' visible={reportCode?.showYear === false}>
                            <DateBox
                                showClearButton={true}
                                value={dateFrom}
                                onValueChanged={(e) => setDateFrom(e.value)}
                            >
                            </DateBox>
                        </Field>
                        <Field label='Date To' visible={reportCode?.showYear === false}>
                            <DateBox
                                showClearButton={true}
                                value={dateTo}
                                onValueChanged={(e) => setDateTo(e.value)}
                            >
                            </DateBox>
                        </Field>
                    </Item>
                    <Item ratio={1}>
                    </Item>
                </Box>
                <Toolbar className='button-toolbar'>
                    <ToolbarItem location='center'>
                        <Button
                            text="Export Report"
                            disabled={reportCode === undefined || exportToExcel}
                            onClick={(e) => {
                                setExportToExcel(true);
                                reportData.refetch();
                            }}>
                        </Button>
                    </ToolbarItem>
                    <ToolbarItem location='center'>
                        <Button
                            text="View Report"
                            disabled={reportCode === undefined || exportToExcel}
                            onClick={(e) => {
                                setPopupTitle(reportCode?.name ?? '');
                                setPopupVisible(true);
                                reportData.refetch();
                            }}>
                        </Button>
                    </ToolbarItem>
                </Toolbar>
            </Panel>
            <Popup
                title={popupTitle}
                visible={popupVisible}
                hideOnOutsideClick={true}
                onHiding={() => setPopupVisible(false)
                }
            >
                <PopupToolbarItem
                    options={{
                        icon: 'dx-icon dx-icon-close',
                        onClick: () => setPopupVisible(false)
                    }}
                    widget="dxButton"
                    location="after"
                />
                <DataGrid
                    columns={reportData.data?.columns ?? []}
                    dataSource={reportData.data?.rows}
                    onContentReady={(e) => {
                        if (exportToExcel) {
                            const workbook = new Workbook();
                            const worksheet = workbook.addWorksheet("Sheet1");
                            const fileName = reportCode?.name.replace(/[^a-z0-9_-]/gi, '_') + '.xlsx';

                            exportDataGrid({
                                component: e.component,
                                worksheet,
                                keepColumnWidths: false
                            }).then(() => {
                                worksheet.columns.forEach(column => {
                                    const lengths = column.values?.map(v => v?.toString().length ?? 0 + 10) ?? [];
                                    column.width = Math.max(Math.max(...lengths.filter(v => typeof v === 'number')) + 2, ((column?.header ?? "").length) + 6)
                                });
                                workbook.xlsx.writeBuffer().then((buffer) => {
                                    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName);
                                });
                                setExportToExcel(false);
                            });
                        }
                    }}
                ></DataGrid>
                <LoadPopupPanel visible={reportData.isLoading}></LoadPopupPanel>
            </Popup>
        </>
    )
}
